import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CalendarIcon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Heading } from "../../ui/heading";
import { Button } from "../../ui/button";
import { Separator } from "../../ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import Sidebar from "../../DashboardNavigation/sidebar";
import Header from "../../DashboardNavigation/header";
import BreadCrumb from "../../breadcrumb";
import { useToast } from "../../ui/use-toast";
import FileUpload from "../../FileUpload/FileUpload";
import {
  ActionFormValues,
  actionSchema,
  updatedActionSchema,
  UpdatedActionSchema,
} from "./action-zod-schema";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Calendar } from "../../ui/calendar";
import { cn } from "../../../lib/utils";
import {
  getActionById,
  handleCreateAction,
  handleUpdateAction,
} from "../../../API/Actions/actionService";
import QuillEditor from "../../../lib/quill/quill";
import { LoadingOverlay } from "../../ui/loading-overlay";

export const ActionForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;
  const router = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const title = initialData
    ? "Modifier l'initiative"
    : "Ajouter une initiative";
  const description = initialData
    ? "Modifier l'initiative"
    : "Ajouter une initiative";

  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<ActionFormValues | UpdatedActionSchema>({
    resolver: zodResolver(initialData ? updatedActionSchema : actionSchema),
    mode: "onChange",
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateAction(initialData, formData, router);
      } else {
        await handleCreateAction(formData, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { title: "initiatives", link: "/admin-dashboard/initiative/" },
    { title: "Ajouter", link: "/admin-dashboard/initiative/ajouter" },
  ];

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const actionData = await getActionById(initialData);
        setData(actionData);
        form.reset({
          title: actionData.title,
          description: actionData.description,
          date: actionData.date ? new Date(actionData.date) : undefined,
          image: actionData.image
            ? [
                {
                  fileName: actionData.image,
                  name: actionData.image,
                  fileSize: 0,
                  size: 0,
                  key: actionData.image,
                },
              ]
            : [],
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  return (
    <>
      <Header />
      <div className="flex h-screen ">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between mb-2">
            <Heading title={title} description={description} />
          </div>
          <Separator className="mb-2" />

          <LoadingOverlay isVisible={loading} />
          <Form {...form}>
            <form className="w-full " onSubmit={form.handleSubmit(onSubmit)}>
              <div className="gap-8 md:grid md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titre </FormLabel>
                      <FormControl>
                        <Input
                          disabled={loading}
                          placeholder="Titre..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col mt-3">
                      <FormLabel>Date Début</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-[280px] justify-start text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {field.value ? (
                              field.value.toLocaleDateString("fr-FR")
                            ) : (
                              <span>Date début</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-5">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <QuillEditor
                          value={field.value || ""}
                          onChange={(value: string) => {
                            field.onChange(value);
                            form.trigger("description");
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {data?.image && (
                <div className="mt-4 mb-4">
                  <img
                    src={`${uploadUrl}/initiatives/${data.image}`}
                    alt="Old Image Preview"
                    className="w-10 h-10 h-auto"
                  />
                </div>
              )}
              <FormField
                control={form.control}
                name="image"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <FileUpload
                        onChange={(files) => {
                          const mappedFiles = files.map((file) => ({
                            key: file.id,
                            fileName: file.fileName,
                            name: file.fileName,
                            fileSize: parseInt(file.fileSize),
                            size: parseInt(file.fileSize),
                            file: file.file,
                          }));
                          form.setValue("image", mappedFiles);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                disabled={loading}
                isLoading={loading}
                className="ml-auto"
                type="submit"
              >
                {action}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
